import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Icon, Input, Modal, RadioGroup, TextArea, Tooltip, useIsMobile } from "@aidkitorg/component-library";
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import type { extractReturnAsJson } from '@aidkitorg/types/lib/endpoints';
import { usePost } from "./API";
import { buildFolderTree, displayFolderPath, folderAndAncestors, folderForEnter, FolderNode, FolderPathNode, folderPathTree, GLOBAL_FOLDER, globalFirst, matchesSearch, movedPath, normalizeFolderPath, QueryScope, scopeTypedPath, searchFolders, splitTreePath, treePath, withGlobalFolder } from "./exploreFolderTree";

type Rows<Path extends keyof Directory> =
  NonNullable<Extract<extractReturnAsJson<Directory[Path]>, { queries: unknown }>['queries']>[number];
export type ProgramExploreQuery = Rows<'/program/admin/explore_query/list'>;
export type GlobalExploreQuery = Rows<'/program/admin/explore_query/global/list'>;
/** A saved query from this program's Postgres or Dynamo's Global folder. */
export type SavedExploreQuery =
  | (ProgramExploreQuery & { scope: 'program' })
  | (GlobalExploreQuery & { scope: 'global' });

/** A query moved by a folder rename: its version before (from) and after (to) the move. */
export type MovedQueryVersion = { id: string, from: string, to: string };

/**
 * A saved query's version: its updated_at as an ISO string. 
 * Updates send back the version the editor opened and are refused with a conflict if someone saved the query since.
 */
export const toVersion = (updatedAt: string | Date) => new Date(updatedAt).toISOString();

/** Link that opens a saved query on /explore (see the `saved` and `scope` param handling in Explore.tsx). */
export function savedQueryLink(id: string, scope: QueryScope) {
  const params = new URLSearchParams({ saved: id });
  if (scope === 'global') params.set('scope', 'global');
  return `${window.location.origin}${window.location.pathname}?${params}`;
}

/** The query's folder as a tree path, so Global's queries sit under the Global folder. */
export const queryTreePath = (q: SavedExploreQuery) => treePath(q.scope, q.folder_name);

/** Every folder for the pickers, including the Global folder even before anything is saved in it. */
export function savedFolderPaths(queries: SavedExploreQuery[]): string[] {
  const paths = new Set<string>([GLOBAL_FOLDER]);
  for (const q of queries) for (const path of folderAndAncestors(queryTreePath(q))) paths.add(path);
  return Array.from(paths).sort((a, b) => a.localeCompare(b));
}

/** Folders in the same place as `scope` (this program, or Global), for picking a folder without changing stores. */
const foldersIn = (folders: string[], scope: QueryScope) => folders.filter(f => splitTreePath(f).scope === scope);

const SAVE_ERRORS: Record<string, string> = {
  name_taken: 'A saved query with this name already exists in this folder.',
  invalid_name: "Enter a name. Names can't contain '/'; use the folder field for folders.",
  invalid_folder: "Folders can't start with \"global\", which is reserved for queries shared across programs.",
  invalid_query: 'There is no query to save yet.',
  permission_denied: "You don't have permission to save queries.",
  not_found: 'This saved query was deleted. Save it as a new query instead.',
  conflict: 'Someone else saved this query since you opened it. Save yours as a new query, or reopen it from the list to see their changes (this discards yours).',
};

const nameError = (name: string) =>
  !name.trim() || name.includes('/') ? SAVE_ERRORS.invalid_name : null;

const saveErrorMessage = (error?: string) =>
  SAVE_ERRORS[error ?? ''] ?? 'Could not save the query. Please try again.';

const ROOT_FOLDER_LABEL = 'No folder';

/** One folder in the picker's tree; subfolders expand in place under a chevron. */
function FolderPickerRow(props: {
  node: FolderPathNode,
  depth: number,
  selected: string,
  isExpanded: (path: string) => boolean,
  toggle: (path: string) => void,
  onSelect: (path: string) => void,
}) {
  const { node } = props;
  const expanded = props.isExpanded(node.path);
  const isSelected = node.path === props.selected;
  return (
    <li>
      <div
        className={`flex items-center gap-1 pr-2 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
        style={{ paddingLeft: `${0.25 + props.depth * 1}rem` }}
      >
        {node.children.length > 0 ? (
          <button
            type="button"
            className="rounded p-1 text-gray-500 hover:bg-gray-100"
            onClick={() => props.toggle(node.path)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.path}`}
          >
            <Icon name={expanded ? "ChevronDownIcon" : "ChevronRightIcon"} size="sm" />
          </button>
        ) : (
          // Keep names aligned with folders that have a chevron.
          <span className="w-7 shrink-0" aria-hidden="true" />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left text-sm text-gray-800"
          onClick={() => props.onSelect(node.path)}
          aria-current={isSelected ? 'true' : undefined}
          title={node.path}
        >
          <Icon name={node.path === GLOBAL_FOLDER ? "GlobeAltIcon" : "FolderIcon"} size="sm" />
          <span className="break-words">{node.path === GLOBAL_FOLDER ? 'Global' : node.name}</span>
          {isSelected && <Icon name="CheckIcon" size="sm" />}
        </button>
      </div>
      {expanded && node.children.length > 0 && (
        <ul>
          {node.children.map(child => <FolderPickerRow key={child.path} {...props} node={child} depth={props.depth + 1} />)}
        </ul>
      )}
    </li>
  );
}

function FolderPicker(props: {
  value: string,
  onChange: (folder: string) => void,
  folders: string[],
  lockedScope?: QueryScope,
  /** Shown when a Global path is typed into a picker locked to this program. */
  globalBlockedMessage?: string,
}) {
  const showProgramRoot = props.lockedScope !== 'global';
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const tree = useMemo(() => globalFirst(folderPathTree(props.folders)), [props.folders]);

  const typed = scopeTypedPath(filter, props.lockedScope);
  const search = { ...searchFolders(props.folders, typed.path), ...(typed.blocked ? { canCreate: false } : {}) };
  const { path: typedPath, matches, canCreate } = search;

  function openPicker() {
    setFilter('');
    // Start with the current folder visible.
    setExpanded(new Set(folderAndAncestors(props.value)));
    setOpen(true);
  }

  function select(path: string) {
    props.onChange(normalizeFolderPath(path));
    setOpen(false);
  }

  // Close when clicking anywhere outside the picker.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const rowProps = {
    depth: 0,
    selected: props.value,
    isExpanded: (path: string) => expanded.has(path),
    toggle: (path: string) => setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    }),
    onSelect: select,
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          // Close just the picker, not the whole dialog.
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <span className="block text-sm font-medium text-gray-900">Folder</span>
      <button
        type="button"
        className="mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
        onClick={() => (open ? setOpen(false) : openPicker())}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <Icon name={splitTreePath(props.value).scope === 'global' ? "GlobeAltIcon" : "FolderIcon"} size="sm" />
          <span className="truncate">{displayFolderPath(props.value) || ROOT_FOLDER_LABEL}</span>
        </span>
        <Icon name="ChevronUpDownIcon" size="sm" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="border-b border-gray-200 p-2">
            <Input
              label="Search folders or type to create a new folder"
              showLabel={false}
              autoFocus
              placeholder="Search, or type a new folder"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                const folder = folderForEnter(search);
                if (folder !== null) select(folder);
              }}
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {typedPath ? (
              <>
                {canCreate && (
                  <li>
                    <button
                      type="button"
                      className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm font-medium text-blue-700 hover:bg-gray-50"
                      onClick={() => select(typedPath)}
                    >
                      <Icon name="PlusIcon" size="sm" />
                      <span className="break-words">Create folder "{displayFolderPath(typedPath)}"</span>
                    </button>
                  </li>
                )}
                {matches.map(path => (
                  <li key={path}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm text-gray-800 ${path === props.value ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => select(path)}
                    >
                      <Icon name={splitTreePath(path).scope === 'global' ? "GlobeAltIcon" : "FolderIcon"} size="sm" />
                      <span className="break-words">{displayFolderPath(path)}</span>
                    </button>
                  </li>
                ))}
                {typed.blocked && (
                  <li className="px-3 py-1.5 text-sm text-gray-500">
                    {props.globalBlockedMessage ?? "Global folders aren't available here. Open the query and save a copy into Global instead."}
                  </li>
                )}
                {!typed.blocked && !canCreate && matches.length === 0 && (
                  <li className="px-3 py-1.5 text-sm text-gray-500">No matching folders.</li>
                )}
              </>
            ) : (
              <>
                {showProgramRoot && (
                  <li>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm text-gray-800 ${props.value === '' ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => select('')}
                      aria-current={props.value === '' ? 'true' : undefined}
                    >
                      {ROOT_FOLDER_LABEL}
                      {props.value === '' && <Icon name="CheckIcon" size="sm" />}
                    </button>
                  </li>
                )}
                {tree.map(node => <FolderPickerRow key={node.path} {...rowProps} node={node} />)}
              </>
            )}
          </ul>
        </div>
      )}
      <p className="mt-1 text-xs text-gray-500">
        {props.lockedScope === 'global'
          ? 'Type a new path to create a folder inside Global, using / for subfolders.'
          : props.lockedScope === 'program'
            ? 'Type a new path to create a folder, using / for subfolders.'
            : <>Type a new path to create a folder, using / for subfolders. <br/> Choose Global to share with every program.</>}
      </p>
    </div>
  );
}

/** Name, folder and description inputs, shared by the save and edit dialogs. */
function QueryDetailsFields(props: {
  name: string,
  setName: (name: string) => void,
  folder: string,
  setFolder: (folder: string) => void,
  folders: string[],
  lockedScope?: QueryScope,
  globalBlockedMessage?: string,
  description: string,
  setDescription: (description: string) => void,
  error: string | null,
  clearError: () => void,
}) {
  return (
    <>
      <Input
        label="Name"
        required
        autoFocus
        value={props.name}
        onChange={(e) => {
          props.setName(e.target.value);
          props.clearError();
        }}
        error={props.error ? { hasError: true, errorMessage: props.error } : undefined}
      />
      <FolderPicker
        value={props.folder}
        onChange={(folder) => {
          props.setFolder(folder);
          props.clearError();
        }}
        folders={props.folders}
        lockedScope={props.lockedScope}
        globalBlockedMessage={props.globalBlockedMessage}
      />
      <TextArea
        label="Description"
        placeholder="What does this query answer?"
        value={props.description}
        onChange={(e) => props.setDescription(e.target.value)}
        rows={3}
      />
    </>
  );
}

export function SaveQueryModal(props: {
  open: boolean,
  onClose: () => void,
  /** Reads the encoded query (URL hash, without the leading #) at submit time. */
  getQuery: () => Promise<string>,
  /** The saved query currently open in the editor, if not null offers updating it instead of saving a new one. */
  openQuery: SavedExploreQuery | null,
  /**
   * The version of openQuery the editor holds, sent with an update so it can't overwrite a newer save.
   * Without it (an old link, or a link still loading) the editor's contents can't be matched to a version, so
   * only saving as a new query is offered.
   */
  openVersion: string | null,
  /** Existing folder paths, offered by the folder picker. */
  folders: string[],
  /** explore-global.edit: whether Global is offered as a place to save. */
  canSaveToGlobal: boolean,
  /** version is the saved query's new version, which the editor now holds. */
  onSaved: (saved: { id: string, scope: QueryScope, name: string, updated: boolean, version: string }) => void,
  /** Someone else saved the open query since it was opened, so the list needs reloading to show their version. */
  onStale: () => void,
}) {
  const createSaved = usePost('/program/admin/explore_query/create', { suppressErrorDisplay: true });
  const updateSaved = usePost('/program/admin/explore_query/update', { suppressErrorDisplay: true });
  const createGlobal = usePost('/program/admin/explore_query/global/create', { suppressErrorDisplay: true });
  const updateGlobal = usePost('/program/admin/explore_query/global/update', { suppressErrorDisplay: true });
  const [mode, setMode] = useState<'update' | 'new'>('new');
  const [name, setName] = useState('');
  const [folder, setFolder] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canUpdate = !!props.openQuery && !!props.openVersion;

  // Default to updating the open saved query each time the dialog opens. A new query defaults to its folder.
  useEffect(() => {
    if (props.open) {
      setMode(canUpdate ? 'update' : 'new');
      setFolder(props.openQuery ? queryTreePath(props.openQuery) : '');
    }
  }, [props.open]);

  function close() {
    setName('');
    setFolder('');
    setDescription('');
    setError(null);
    props.onClose();
  }

  // Cancel, ESC, and click outside of popup are ignored while saving.
  function cancel() {
    if (!saving) close();
  }

  async function save() {
    if (mode === 'update' && props.openQuery && props.openVersion) {
      const openQuery = props.openQuery;
      const openVersion = props.openVersion;
      setSaving(true);
      try {
        const update = openQuery.scope === 'global' ? updateGlobal : updateSaved;
        const result = await update({ id: openQuery.id, expected_updated_at: openVersion, query: await props.getQuery() });
        if (!result?.updated_at) {
          setError(saveErrorMessage(result?.error));
          if (result?.error === 'not_found') setMode('new');
          if (result?.error === 'conflict') props.onStale();
          return;
        }
        props.onSaved({ id: openQuery.id, scope: openQuery.scope, name: openQuery.name, updated: true, version: toVersion(result.updated_at) });
        close();
      } catch {
        setError(saveErrorMessage());
      } finally {
        setSaving(false);
      }
      return;
    }

    const invalid = nameError(name);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    try {
      const target = splitTreePath(folder);
      const create = target.scope === 'global' ? createGlobal : createSaved;
      const result = await create({ name, folder_name: target.folder, description, query: await props.getQuery() });
      if (!result || 'error' in result || !result.id || !result.updated_at) {
        setError(saveErrorMessage(result && 'error' in result ? result.error : undefined));
        return;
      }
      props.onSaved({ id: result.id, scope: target.scope, name: name.trim(), updated: false, version: toVersion(result.updated_at) });
      close();
    } catch {
      setError(saveErrorMessage());
    } finally {
      setSaving(false);
    }
  }

  const updating = mode === 'update' && canUpdate;

  return (
    <Modal open={props.open} onClose={cancel} title="Save query" size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <p className="text-sm text-gray-600">
          Saved queries are shared with everyone in this program who can use Explore.
          {props.canSaveToGlobal && ' Save into the Global folder to share with every program.'}
        </p>
        {props.openQuery && !canUpdate && (
          <p className="text-sm text-gray-600">
            To update &quot;{props.openQuery.name}&quot;, reopen it from the list first. You can save this as a new query.
          </p>
        )}
        {canUpdate && props.openQuery && (
          <RadioGroup
            label="Save as"
            value={mode}
            onChange={(value) => {
              setMode(value as 'update' | 'new');
              setError(null);
            }}
            options={[
              {
                value: 'update',
                label: `Update "${props.openQuery.name}"`,
                description: 'Replace this saved query with what is in the editor now.',
              },
              { value: 'new', label: 'Save as a new query' },
            ]}
          />
        )}
        {updating
          ? error && <p className="text-sm text-red-600" role="alert">{error}</p>
          : <QueryDetailsFields
            name={name}
            setName={setName}
            folder={folder}
            setFolder={setFolder}
            folders={props.canSaveToGlobal ? props.folders : foldersIn(props.folders, 'program')}
            lockedScope={props.canSaveToGlobal ? undefined : 'program'}
            globalBlockedMessage="You don't have permission to save into Global."
            description={description}
            setDescription={setDescription}
            error={error}
            clearError={() => setError(null)}
          />}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={cancel} isDisabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving} isDisabled={saving}>
            {updating ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function EditSavedQueryModal(props: {
  /** The query being edited; the modal is open while this is set. */
  query: SavedExploreQuery | null,
  folders: string[],
  /** explore-global.edit: decides what the folder picker says about Global. */
  canEditGlobal: boolean,
  onClose: () => void,
  /** versions: the version that was edited, and the version the edit created. */
  onUpdated: (query: SavedExploreQuery, versions: { from: string, to: string }) => void,
  /** Someone else changed the query since the list loaded, so the list needs reloading. */
  onStale: () => void,
}) {
  const updateSaved = usePost('/program/admin/explore_query/update', { suppressErrorDisplay: true });
  const updateGlobal = usePost('/program/admin/explore_query/global/update', { suppressErrorDisplay: true });
  const [name, setName] = useState('');
  const [folder, setFolder] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (props.query) {
      setName(props.query.name);
      setFolder(queryTreePath(props.query));
      setDescription(props.query.description);
      setError(null);
    }
  }, [props.query]);

  async function save() {
    const query = props.query;
    if (!query) return;
    const invalid = nameError(name);
    if (invalid) {
      setError(invalid);
      return;
    }
    // Queries can't move between this program and Global, only within one of them.
    const target = splitTreePath(folder);
    if (target.scope !== query.scope) {
      setError('To move a query between this program and Global, open it and save it as a new query there.');
      return;
    }
    setSaving(true);
    try {
      const update = query.scope === 'global' ? updateGlobal : updateSaved;
      const from = toVersion(query.updated_at);
      const result = await update({ id: query.id, expected_updated_at: from, name, folder_name: target.folder, description });
      if (!result?.updated_at) {
        if (result?.error === 'conflict') {
          setError('Someone else changed this query since the list loaded. The list has been reloaded, so close this and edit it again.');
          props.onStale();
          return;
        }
        setError(result?.error === 'not_found'
          ? 'This saved query was deleted.'
          : saveErrorMessage(result?.error));
        return;
      }
      props.onUpdated(
        { ...query, name: name.trim(), folder_name: target.folder, description: description.trim() },
        { from, to: toVersion(result.updated_at) },
      );
      props.onClose();
    } catch {
      setError(saveErrorMessage());
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={!!props.query} onClose={props.onClose} title="Edit saved query" size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <QueryDetailsFields
          name={name}
          setName={setName}
          folder={folder}
          setFolder={setFolder}
          folders={props.query ? foldersIn(props.folders, props.query.scope) : props.folders}
          lockedScope={props.query?.scope}
          globalBlockedMessage={props.canEditGlobal ? undefined : "You don't have permission to save into Global."}
          description={description}
          setDescription={setDescription}
          error={error}
          clearError={() => setError(null)}
        />
        <p className="text-xs text-gray-500">
          To change the query contents, open it, edit it, then use Save.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={props.onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving} isDisabled={saving}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

export function DeleteSavedQueryModal(props: {
  query: SavedExploreQuery | null,
  onClose: () => void,
  onDeleted: (query: SavedExploreQuery) => void,
}) {
  const deleteSaved = usePost('/program/admin/explore_query/delete', { suppressErrorDisplay: true });
  const deleteGlobal = usePost('/program/admin/explore_query/global/delete', { suppressErrorDisplay: true });
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keep showing the last query while the modal animates closed, instead of a blank name.
  const [shown, setShown] = useState(props.query);
  useEffect(() => {
    if (props.query) setShown(props.query);
  }, [props.query]);

  function close() {
    setError(null);
    props.onClose();
  }

  async function confirmDelete() {
    const query = props.query;
    if (!query) return;
    setDeleting(true);
    try {
      const result = await (query.scope === 'global' ? deleteGlobal : deleteSaved)({ id: query.id });
      // not_found means someone else already deleted it, which is the outcome the user asked for.
      if (result && ('ok' in result || result.error === 'not_found')) {
        props.onDeleted(query);
        close();
        return;
      }
      setError(result?.error === 'permission_denied'
        ? "You don't have permission to delete saved queries."
        : 'Could not delete the query. Please try again.');
    } catch {
      setError('Could not delete the query. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal open={!!props.query} onClose={close} title="Delete saved query?" size="sm">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-700">
          <strong className="break-words">{shown?.name}</strong> will be deleted for everyone in
          {shown?.scope === 'global' ? ' every program' : ' this program'}.
          This can't be undone.
        </p>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
          <Button type="button" variant="danger" onClick={confirmDelete} loading={deleting} isDisabled={deleting}>
            Delete
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const RENAME_FOLDER_ERRORS: Record<string, string> = {
  name_taken: 'A query in this folder has the same name as one already at the new location. Rename it first.',
  invalid_folder: "Enter a new path. It can't be inside this folder or start with \"global\".",
  not_found: 'This folder no longer exists.',
  permission_denied: "You don't have permission to rename folders.",
  conflict: 'Someone changed a query in this folder while it was being moved, so nothing moved. Try again.',
};

export function RenameFolderModal(props: {
  /** Tree path of the folder being renamed; the modal is open while this is set. */
  folder: string | null,
  onClose: () => void,
  /** versions: each moved query's version before and after the move. */
  onRenamed: (from: string, to: string, versions: MovedQueryVersion[]) => void,
}) {
  const renameFolder = usePost('/program/admin/explore_query/rename_folder', { suppressErrorDisplay: true });
  const renameGlobalFolder = usePost('/program/admin/explore_query/global/rename_folder', { suppressErrorDisplay: true });
  const [to, setTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A Global folder is renamed (and can only move) within Global, so its path is edited relative to Global.
  const source = props.folder ? splitTreePath(props.folder) : null;
  const inGlobal = source?.scope === 'global';

  useEffect(() => {
    if (props.folder) {
      setTo(splitTreePath(props.folder).folder);
      setError(null);
    }
  }, [props.folder]);

  async function save() {
    if (!props.folder || !source) return;
    const from = source.folder;
    const target = normalizeFolderPath(to);
    if (target === from) {
      props.onClose();
      return;
    }
    setSaving(true);
    try {
      const result = await (inGlobal ? renameGlobalFolder : renameFolder)({ from, to: target });
      if (!result || 'error' in result) {
        setError(result && 'error' in result && result.error === 'partially_moved' && 'moved' in result
          ? `Only ${result.moved} queries moved, because someone changed the folder at the same time. Rename it again to finish.`
          : RENAME_FOLDER_ERRORS[result?.error ?? ''] ?? 'Could not rename the folder. Please try again.');
        return;
      }
      props.onRenamed(props.folder, treePath(source.scope, target), result.versions ?? []);
      props.onClose();
    } catch {
      setError('Could not rename the folder. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={!!props.folder} onClose={props.onClose} title="Rename folder" size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Input
          label={inGlobal ? 'Folder path inside Global' : 'Folder path'}
          required
          autoFocus
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            setError(null);
          }}
          error={error ? { hasError: true, errorMessage: error } : undefined}
        />
        <p className="text-xs text-gray-500">
          Everything in the folder, including subfolders, moves with it. Change the path to move it.
        </p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={props.onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving} isDisabled={saving}>Rename</Button>
        </div>
      </form>
    </Modal>
  );
}

const EXPANDED_STORAGE_KEY = 'exploreSavedQueriesExpanded';
const OPEN_FOLDERS_STORAGE_KEY = 'exploreSavedQueriesOpenFolders';

function readStoredExpanded(fallback: boolean) {
  try {
    const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
}

function readStoredOpenFolders(): Set<string> {
  try {
    const stored = JSON.parse(localStorage.getItem(OPEN_FOLDERS_STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(stored) ? stored.filter((p): p is string => typeof p === 'string') : []);
  } catch {
    return new Set();
  }
}

function storeOpenFolders(folders: Set<string>) {
  try {
    localStorage.setItem(OPEN_FOLDERS_STORAGE_KEY, JSON.stringify(Array.from(folders)));
  } catch {
    // Storage can be unavailable (e.g. incognito mode)
  }
}

const indent = (depth: number) => ({ paddingLeft: `${1 + depth * 1}rem` });

type TreeItem = { name: string, description: string, folder_name: string, query: SavedExploreQuery };

type QueryKey = { id: string, scope: QueryScope };
const isSameQuery = (q: SavedExploreQuery, key?: QueryKey | null) => !!key && q.id === key.id && q.scope === key.scope;

type RowActions = {
  currentUserId?: string,
  openQueryKey?: QueryKey | null,
  onOpen: (query: SavedExploreQuery) => void,
  onCopyLink: (query: SavedExploreQuery) => void,
  onEdit: (query: SavedExploreQuery) => void,
  onDelete: (query: SavedExploreQuery) => void,
  onRenameFolder: (path: string) => void,
  isFolderOpen: (path: string) => boolean,
  toggleFolder: (path: string) => void,
  /** While searching or filtering every folder is shown open, so folders can't be toggled. */
  filtering: boolean,
  /** explore-global.edit: without it, Global queries and folders are read-only. */
  canEditGlobal: boolean,
};

/** 
 * A query's description that is cut off due to being too long will have the full text in a tooltip on hover
 */
function QueryDescription(props: { description: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setTruncated(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [props.description, truncated]);

  const text = <p ref={ref} className="line-clamp-2 text-sm text-gray-600">{props.description}</p>;
  if (!truncated) return text;
  return (
    <Tooltip
      asChild
      side="right"
      align="start"
      text={props.description}
      contentClassName="max-w-xs whitespace-pre-wrap break-words font-normal"
    >
      {text}
    </Tooltip>
  );
}

function QueryRow(props: { query: SavedExploreQuery, depth: number, actions: RowActions }) {
  const { query: q, actions } = props;
  const isOpen = isSameQuery(q, actions.openQueryKey);
  return (
    <li
      className={`group flex items-start justify-between gap-2 py-2 pr-4 ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
      style={indent(props.depth)}
      aria-current={isOpen ? 'true' : undefined}
    >
      <div className="min-w-0">
        <button
          type="button"
          className="break-words text-left font-medium text-blue-700 hover:underline"
          onClick={() => actions.onOpen(q)}
        >
          {q.name}
        </button>
        {q.description && <QueryDescription description={q.description} />}
        <p className="text-xs text-gray-500">
          {q.created_by === actions.currentUserId ? 'You' : (q.created_by_name ?? 'A former user')}
          {' · '}{new Date(q.updated_at).toLocaleDateString()}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button
          type="button"
          variant="unstyled"
          size="sm"
          onClick={() => actions.onCopyLink(q)}
          icon={<Icon name="LinkIcon" size="sm" />}
          aria-label={`Copy link to ${q.name}`}
          title="Copy link"
        />
        {(q.scope !== 'global' || actions.canEditGlobal) && (
          <>
            <Button
              type="button"
              variant="unstyled"
              size="sm"
              onClick={() => actions.onEdit(q)}
              icon={<Icon name="PencilIcon" size="sm" />}
              aria-label={`Edit ${q.name}`}
              title="Edit name, folder and description"
            />
            <Button
              type="button"
              variant="unstyled"
              size="sm"
              className="text-gray-500 hover:text-red-600"
              onClick={() => actions.onDelete(q)}
              icon={<Icon name="TrashIcon" size="sm" />}
              aria-label={`Delete ${q.name}`}
              title="Delete"
            />
          </>
        )}
      </div>
    </li>
  );
}

/** A folder's subfolders then its queries. */
function FolderContents(props: { node: FolderNode<TreeItem>, depth: number, actions: RowActions }) {
  const folders = props.depth === 0 ? globalFirst(props.node.folders) : props.node.folders;
  return (
    <>
      {folders.map(folder => (
        <FolderRow key={folder.path} node={folder} depth={props.depth} actions={props.actions} />
      ))}
      {props.node.queries.map(item => (
        <QueryRow key={`${item.query.scope}:${item.query.id}`} query={item.query} depth={props.depth} actions={props.actions} />
      ))}
    </>
  );
}

function FolderRow(props: { node: FolderNode<TreeItem>, depth: number, actions: RowActions }) {
  const { node, actions } = props;
  const open = actions.isFolderOpen(node.path);
  const isGlobalRoot = node.path === GLOBAL_FOLDER;
  return (
    <li>
      <div className="group flex items-center justify-between gap-2 py-2 pr-4 hover:bg-gray-50" style={indent(props.depth)}>
        <button
          type="button"
          className="flex min-w-0 items-center gap-1.5 text-left font-medium text-gray-800 disabled:cursor-default"
          onClick={() => actions.toggleFolder(node.path)}
          disabled={actions.filtering}
          aria-expanded={open}
          title={isGlobalRoot ? 'Shared with every program' : displayFolderPath(node.path)}
        >
          {!actions.filtering && <Icon name={open ? "ChevronDownIcon" : "ChevronRightIcon"} size="sm" />}
          <Icon name={isGlobalRoot ? "GlobeAltIcon" : open ? "FolderOpenIcon" : "FolderIcon"} size="sm" />
          <span className="break-words">{isGlobalRoot ? 'Global' : node.name}</span>
          <span className="shrink-0 text-xs font-normal text-gray-500">({node.count})</span>
        </button>
        {/* Global itself can't be renamed, only the folders inside it (and only with explore-global.edit). */}
        {!isGlobalRoot && (splitTreePath(node.path).scope !== 'global' || actions.canEditGlobal) && (
          <Button
            type="button"
            variant="unstyled"
            size="sm"
            onClick={() => actions.onRenameFolder(node.path)}
            icon={<Icon name="PencilIcon" size="sm" />}
            aria-label={`Rename folder ${displayFolderPath(node.path)}`}
            title="Rename or move folder"
          />
        )}
      </div>
      {open && (
        <ul className="divide-y divide-gray-100">
          <FolderContents node={node} depth={props.depth + 1} actions={actions} />
          {/* Only Global can be empty: it's shown before anything is saved in it. */}
          {node.count === 0 && (
            <li className="py-2 pr-4 text-sm text-gray-500" style={indent(props.depth + 1)}>
              Nothing in Global yet.
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

/** Collapsible left-hand menu of saved queries on /explore */
export function SavedQueriesSidebar(props: {
  queries: SavedExploreQuery[],
  folders: string[],
  currentUserId?: string,
  onOpen: (query: SavedExploreQuery) => void,
  onCopyLink: (query: SavedExploreQuery) => void,
  onDeleted: (query: SavedExploreQuery) => void,
  onUpdated: (query: SavedExploreQuery, versions: { from: string, to: string }) => void,
  /** The list is out of date (an edit hit a conflict) and should be reloaded. */
  onStale: () => void,
  /** versions: each moved query's version before and after the move. */
  onFolderRenamed: (from: string, to: string, versions: MovedQueryVersion[]) => void,
  /** The saved query open in the editor, highlighted in the list. */
  openQueryKey?: QueryKey | null,
  /** explore-global.view: show the Global folder even before anything is saved in it. */
  canViewGlobal: boolean,
  /** explore-global.edit: allow editing, deleting and renaming in Global. */
  canEditGlobal: boolean,
}) {
  const isMobile = useIsMobile();
  const [pendingDelete, setPendingDelete] = useState<SavedExploreQuery | null>(null);
  const [pendingEdit, setPendingEdit] = useState<SavedExploreQuery | null>(null);
  const [pendingRename, setPendingRename] = useState<string | null>(null);
  const [expanded, setExpandedState] = useState(() => readStoredExpanded(!isMobile));
  const [openFolders, setOpenFoldersState] = useState(readStoredOpenFolders);
  const [search, setSearch] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  function setExpanded(value: boolean) {
    setExpandedState(value);
    try {
      localStorage.setItem(EXPANDED_STORAGE_KEY, String(value));
    } catch {
      // Storage can be unavailable (e.g. incognito mode)
    }
  }

  function setOpenFolders(update: (folders: Set<string>) => Set<string>) {
    setOpenFoldersState(prev => {
      const next = update(prev);
      storeOpenFolders(next);
      return next;
    });
  }

  // Open the folders containing the query in the editor, so it's visible in the tree.
  const openQuery = props.queries.find(q => isSameQuery(q, props.openQueryKey));
  const openQueryFolder = openQuery ? queryTreePath(openQuery) : undefined;
  useEffect(() => {
    if (!openQueryFolder) return;
    const ancestors = folderAndAncestors(openQueryFolder);
    if (ancestors.some(path => !openFolders.has(path))) {
      setOpenFolders(prev => new Set(Array.from(prev).concat(ancestors)));
    }
  }, [openQueryFolder]);

  const filtering = onlyMine || search.trim() !== '';
  const tree = useMemo(() => {
    const built = buildFolderTree(props.queries
      .filter(q => !onlyMine || q.created_by === props.currentUserId)
      .map((q): TreeItem => ({ name: q.name, description: q.description, folder_name: queryTreePath(q), query: q }))
      // Matching the tree path means searching "global" finds every Global query.
      .filter(item => matchesSearch(item, search)));
    // Show the Global folder (to anyone who has read access), even before anything is saved there.
    return filtering || !props.canViewGlobal ? built : withGlobalFolder(built);
  }, [props.queries, props.currentUserId, search, onlyMine, filtering, props.canViewGlobal]);

  const actions: RowActions = {
    currentUserId: props.currentUserId,
    openQueryKey: props.openQueryKey,
    onOpen: props.onOpen,
    onCopyLink: props.onCopyLink,
    onEdit: setPendingEdit,
    onDelete: setPendingDelete,
    onRenameFolder: setPendingRename,
    // While filtering, the tree only holds folders with matches, so show them all open.
    isFolderOpen: (path) => filtering || openFolders.has(path),
    filtering,
    canEditGlobal: props.canEditGlobal,
    // Ignored while filtering, so a click that has no visible effect doesn't change the saved open folders.
    toggleFolder: (path) => {
      if (filtering) return;
      setOpenFolders(prev => {
        const next = new Set(prev);
        if (next.has(path)) {
          next.delete(path);
        } else {
          next.add(path);
        }
        return next;
      });
    },
  };

  if (!expanded) {
    return (
      <aside className="shrink-0 border-b border-gray-200 bg-white md:border-b-0 md:border-r">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-3 text-gray-700 hover:bg-gray-50 md:h-full md:w-12 md:flex-col md:px-0 md:py-4"
          onClick={() => setExpanded(true)}
          aria-expanded={false}
          aria-label={`Show saved queries`}
          title="Show saved queries"
        >
          <Icon name="ChevronDoubleRightIcon" size="sm" />
          <span className="text-sm font-medium md:[writing-mode:vertical-rl]">
            Saved queries
          </span>
        </button>
      </aside>
    );
  }

  return (
    <aside
      className="flex shrink-0 flex-col border-b border-gray-200 bg-white md:sticky md:top-0 md:h-screen md:w-72 md:border-b-0 md:border-r"
      aria-label="Saved queries"
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <h2 className="text-base font-medium text-gray-900">Saved queries</h2>
        <Button
          type="button"
          variant="unstyled"
          size="sm"
          onClick={() => setExpanded(false)}
          icon={<Icon name="ChevronDoubleLeftIcon" size="sm" />}
          aria-expanded={true}
          aria-label="Hide saved queries"
          title="Hide saved queries"
        />
      </div>
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3">
        <Input
          label="Search saved queries"
          showLabel={false}
          placeholder="Search by name, description or folder"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Checkbox label="Only mine" checked={onlyMine} onChange={(c) => setOnlyMine(!!c)} />
      </div>
      <ul className="max-h-80 flex-1 divide-y divide-gray-100 overflow-y-auto md:max-h-none">
        <FolderContents node={tree} depth={0} actions={actions} />
        {tree.count === 0 && (
          <li className="px-4 py-3 text-sm text-gray-500">
            {props.queries.length === 0 ? 'No saved queries yet. Use Save to add one.' : 'No saved queries match.'}
          </li>
        )}
      </ul>
      <EditSavedQueryModal
        query={pendingEdit}
        folders={props.folders}
        canEditGlobal={props.canEditGlobal}
        onClose={() => setPendingEdit(null)}
        onUpdated={props.onUpdated}
        onStale={props.onStale}
      />
      <DeleteSavedQueryModal
        query={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onDeleted={props.onDeleted}
      />
      <RenameFolderModal
        folder={pendingRename}
        onClose={() => setPendingRename(null)}
        onRenamed={(from, to, versions) => {
          // Keep the moved folders open at their new paths.
          setOpenFolders(prev => new Set(Array.from(prev, path => movedPath(path, from, to))));
          props.onFolderRenamed(from, to, versions);
        }}
      />
    </aside>
  );
}
